import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useSubscription } from '../../../contexts/SubscriptionContext';
import { useDemo } from '../../../contexts/DemoContext';

const IncomeSourceTable = ({ incomeSources, onEdit, onDelete, activeTab }) => {
  const { canWrite } = useSubscription();
  const { isDemoModeActive } = useDemo();
  const effectiveCanWrite = canWrite && !isDemoModeActive;
  const [sortField, setSortField] = useState('description');
  const [sortDirection, setSortDirection] = useState('asc');

  const filteredSources = activeTab === 'all' 
    ? incomeSources 
    : incomeSources.filter(source => source.type === activeTab);

  const sortedSources = [...filteredSources].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];

    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getIncomeTypeLabel = (type) => {
    const labels = {
      salary: 'Salary',
      business: 'Business',
      rental: 'Rental',
      passive: 'Passive'
    };
    return labels[type] || type;
  };

  const getFrequencyLabel = (frequency) => {
    const labels = {
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      annually: 'Annually',
      'one-time': 'One-time'
    };
    return labels[frequency] || frequency;
  };

  const calculateAnnualAmount = (amount, frequency) => {
    const multipliers = {
      monthly: 12,
      quarterly: 4,
      annually: 1,
      'one-time': 1
    };
    return amount * (multipliers[frequency] || 1);
  };

  const SortableHeader = ({ field, children }) => (
    <th 
      className="px-4 py-3 text-left text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors duration-200"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-2">
        {children}
        <div className="flex flex-col">
          <Icon 
            name="ChevronUp" 
            size={12} 
            className={`${sortField === field && sortDirection === 'asc' ? 'text-primary' : 'text-border'}`}
          />
          <Icon 
            name="ChevronDown" 
            size={12} 
            className={`${sortField === field && sortDirection === 'desc' ? 'text-primary' : 'text-border'} -mt-1`}
          />
        </div>
      </div>
    </th>
  );

  if (sortedSources.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border p-12 text-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center">
            <Icon name="TrendingUp" size={24} className="text-muted-foreground" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-foreground mb-2">
              {activeTab === 'all' ? 'No Income Sources' : `No ${getIncomeTypeLabel(activeTab)} Income`}
            </h3>
            <p className="text-muted-foreground">
              {activeTab === 'all' ?'Start by adding your first income source to begin planning your financial future.'
                : `Add your first ${getIncomeTypeLabel(activeTab).toLowerCase()} income source to get started.`
              }
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <SortableHeader field="description">Description</SortableHeader>
              <SortableHeader field="type">Type</SortableHeader>
              <SortableHeader field="currentAmount">Amount</SortableHeader>
              <SortableHeader field="frequency">Frequency</SortableHeader>
              <SortableHeader field="startYear">Duration</SortableHeader>
              <SortableHeader field="growthRate">Growth</SortableHeader>
              <SortableHeader field="probability">Probability</SortableHeader>
              <th className="px-4 py-3 text-right text-sm font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedSources.map((source) => (
              <tr key={source.id} className="hover:bg-muted/30 transition-colors duration-200">
                <td className="px-4 py-4">
                  <div className="flex flex-col">
                    <span className="font-medium text-foreground">{source.description}</span>
                    {source.notes && (
                      <span className="text-sm text-muted-foreground mt-1">{source.notes}</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-4">
                  <span className={`
                    inline-flex items-center px-2 py-1 rounded-full text-xs font-medium
                    ${source.type === 'salary' ? 'bg-primary/10 text-primary' : ''}
                    ${source.type === 'business' ? 'bg-accent/10 text-accent' : ''}
                    ${source.type === 'rental' ? 'bg-warning/10 text-warning' : ''}
                    ${source.type === 'passive' ? 'bg-secondary/10 text-secondary' : ''}
                  `}>
                    {getIncomeTypeLabel(source.type)}
                  </span>
                </td>
                <td className="px-4 py-4">
                  <div className="flex flex-col">
                    <span className="font-medium text-foreground">
                      {formatCurrency(source.currentAmount)}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {formatCurrency(calculateAnnualAmount(source.currentAmount, source.frequency))} annually
                    </span>
                  </div>
                </td>
                <td className="px-4 py-4 text-sm text-foreground">
                  {getFrequencyLabel(source.frequency)}
                </td>
                <td className="px-4 py-4 text-sm text-foreground">
                  {source.startYear} - {source.endYear}
                  <div className="text-xs text-muted-foreground">
                    {source.endYear - source.startYear + 1} years
                  </div>
                </td>
                <td className="px-4 py-4 text-sm text-foreground">
                  {source.growthRate}% annually
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center gap-2">
                    <div className={`
                      w-2 h-2 rounded-full
                      ${source.probability >= 80 ? 'bg-success' : ''}
                      ${source.probability >= 60 && source.probability < 80 ? 'bg-warning' : ''}
                      ${source.probability < 60 ? 'bg-destructive' : ''}
                    `} />
                    <span className="text-sm text-foreground">{source.probability}%</span>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEdit(source)}
                      className="text-muted-foreground hover:text-foreground disabled:opacity-60"
                      disabled={!effectiveCanWrite}
                      title={isDemoModeActive ? 'Cannot edit in demo mode' : 'Edit'}
                    >
                      <Icon name="Edit2" size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDelete(source.id)}
                      className="text-muted-foreground hover:text-destructive disabled:opacity-60"
                      disabled={!effectiveCanWrite}
                      title={isDemoModeActive ? 'Cannot delete in demo mode' : 'Delete'}
                    >
                      <Icon name="Trash2" size={16} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default IncomeSourceTable;